import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Phone, CalendarCheck } from "lucide-react";
import { COMPANY } from "../../data/content";

const TRUST_BULLETS = [
  "Mon\u2013Fri 9:00 AM \u2013 3:00 PM",
  "30-minute appointments \u2014 book online",
  "Digital Live Scan + traditional ink cards",
  "FDLE digital submissions available",
];

export const Hero = () => {
  const scrollToBooking = () => {
    document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="hero-bg text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <p className="text-[color:var(--brand-sky)] font-medium text-sm sm:text-base tracking-wide uppercase mb-4">
              The Original Gold Standard for Personal Identification
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]">
              Fingerprinting Services in the Cayman Islands
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
              Completely unique. Established at birth. Nearly impossible to fake. At Cayman Fingerprinting Services, we
              combine the latest technological advances with old-fashioned values like courtesy, professionalism, and
              attention to detail &mdash; for a pleasant, efficient, and safely confidential experience.
            </p>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl">
              {TRUST_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-white/90">
                  <CheckCircle2 className="h-4.5 w-4.5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" style={{ height: 18, width: 18 }} />
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="bg-[color:var(--brand-sky)] hover:bg-[#1791c5] text-white font-semibold h-12 px-8 btn-lift"
                onClick={scrollToBooking}
                data-testid="hero-schedule-appointment-button"
              >
                <CalendarCheck className="h-5 w-5 mr-2" /> Schedule an Appointment
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 btn-lift"
                asChild
                data-testid="hero-call-button"
              >
                <a href={COMPANY.phoneHref}>
                  <Phone className="h-5 w-5 mr-2" /> Call {COMPANY.phone}
                </a>
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5 hidden lg:block"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
          >
            <div className="relative">
              <div className="rounded-2xl shadow-2xl w-full h-[420px] overflow-hidden relative bg-gradient-to-br from-[#123A63] via-[#0B1F3B] to-[#081627] border border-white/10 flex items-center justify-center">
                {/* Fingerprint motif */}
                <svg viewBox="0 0 300 300" className="w-[280px] h-[280px]" fill="none" aria-hidden="true">
                  <g stroke="#1EA7E1" strokeWidth="5" strokeLinecap="round">
                    <circle cx="150" cy="150" r="24" strokeDasharray="90 30" opacity="0.95" />
                    <circle cx="150" cy="150" r="44" strokeDasharray="60 22 110 30" opacity="0.85" />
                    <circle cx="150" cy="150" r="64" strokeDasharray="120 26 70 34" opacity="0.7" />
                    <circle cx="150" cy="150" r="84" strokeDasharray="80 30 140 40" opacity="0.55" />
                    <circle cx="150" cy="150" r="104" strokeDasharray="150 36 90 44" opacity="0.4" />
                    <circle cx="150" cy="150" r="124" strokeDasharray="100 40 170 50" opacity="0.28" />
                  </g>
                  <g stroke="#BFE9F2" strokeWidth="3" strokeLinecap="round" opacity="0.5">
                    <circle cx="150" cy="150" r="34" strokeDasharray="70 40" />
                    <circle cx="150" cy="150" r="54" strokeDasharray="40 30 90 40" />
                    <circle cx="150" cy="150" r="74" strokeDasharray="110 36 60 40" />
                    <circle cx="150" cy="150" r="94" strokeDasharray="70 44 130 50" />
                    <circle cx="150" cy="150" r="114" strokeDasharray="130 46 80 54" />
                  </g>
                </svg>
                {/* Soft glow */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(360px circle at 50% 45%, rgba(30,167,225,0.16), transparent 70%)" }} />
                {/* Floating service chips */}
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm border border-white/15 rounded-lg px-3.5 py-2 text-xs font-medium text-white flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-sky)]" /> Live Scan Digital
                </div>
                <div className="absolute top-1/2 -translate-y-1/2 left-6 bg-white/10 backdrop-blur-sm border border-white/15 rounded-lg px-3.5 py-2 text-xs font-medium text-white flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-seafoam)]" /> Ink Fingerprint Cards
                </div>
                <div className="absolute bottom-16 right-8 bg-white/10 backdrop-blur-sm border border-white/15 rounded-lg px-3.5 py-2 text-xs font-medium text-white flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-sky)]" /> FDLE Submissions
                </div>
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg px-5 py-4 text-[color:var(--brand-navy)]">
                <p className="font-heading font-semibold text-sm">Trusted since day one</p>
                <p className="text-xs text-muted-foreground mt-0.5">Fingerprints &mdash; legally admissible ID since 1911</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
